import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RefreshCw, Search, Settings2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminListUserModules, adminSetUserModules, adminRecomputePrice,
} from "@/lib/admin-modules.functions";
import { BASE_PLANS, MODULES, computePrice, type BasePlanId, type BillingCycle } from "@/lib/billing/modules";

export const Route = createFileRoute("/_authenticated/app/admin/modules")({
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", u.user.id);
    if (!roles?.some((r) => r.role === "admin")) throw redirect({ to: "/app" });
  },
  component: AdminModulesPage,
});

type Row = {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  plan: string | null;
  billing_cycle: string | null;
  active_modules: string[] | null;
  total: number;
  subs: any[];
};

function AdminModulesPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [plan, setPlan] = useState<BasePlanId>("starter");
  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const [mods, setMods] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [recomputed, setRecomputed] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListUserModules({ data: { search: search || undefined, limit: 200 } });
      setRows(data as any);
    } catch (e: any) {
      toast.error(e?.message ?? t("pages.admin.loadError"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openEdit = (r: Row) => {
    setEditing(r);
    setPlan((r.plan as BasePlanId) ?? "starter");
    setCycle((r.billing_cycle as BillingCycle) ?? "monthly");
    setMods(r.active_modules ?? []);
    setRecomputed(null);
  };

  const localBreakdown = useMemo(() => computePrice(plan, mods, cycle), [plan, mods, cycle]);

  const recompute = async () => {
    try {
      const r = await adminRecomputePrice({ data: { plan, billingCycle: cycle, activeModules: mods } });
      setRecomputed(r.total);
      toast.success(t("pages.admin.modulesPage.recomputed", { total: r.total.toFixed(2) }));
    } catch (e: any) {
      toast.error(e?.message ?? t("pages.admin.modulesPage.recomputeError"));
    }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await adminSetUserModules({ data: { userId: editing.id, plan, billingCycle: cycle, activeModules: mods } });
      toast.success(t("pages.admin.modulesPage.updated"));
      setEditing(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? t("pages.admin.modulesPage.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id: string) => setMods((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <>
      <PageHeader
        title={t("pages.admin.modulesPage.title")}
        description={t("pages.admin.modulesPage.description")}
        actions={
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm"><Link to="/app/admin">{t("pages.admin.adminBack")}</Link></Button>
            <Button asChild variant="outline" size="sm"><Link to="/app/admin/module-sync">{t("pages.admin.modulesPage.auditSync")}</Link></Button>
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-2 h-4 w-4" />{t("pages.admin.reload")}</Button>
          </div>
        }
      />
      <PageBody>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t("pages.admin.modulesPage.users")}</span>
              <div className="relative w-72">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()} placeholder={t("pages.admin.modulesPage.searchPh")} className="pl-8 h-9" />
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <p className="text-sm text-muted-foreground">{t("pages.admin.loading")}</p> : (
              <div className="max-h-[70vh] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("pages.admin.cols.user")}</TableHead>
                      <TableHead>{t("pages.admin.cols.plan")}</TableHead>
                      <TableHead>{t("pages.admin.cols.cycle")}</TableHead>
                      <TableHead>{t("pages.admin.cols.modules")}</TableHead>
                      <TableHead>{t("pages.admin.cols.totalPerMonth")}</TableHead>
                      <TableHead className="text-right">{t("pages.admin.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <div className="font-medium">{[r.first_name, r.last_name].filter(Boolean).join(" ") || "-"}</div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{r.plan ?? "starter"}</Badge></TableCell>
                        <TableCell className="text-xs">{r.billing_cycle ?? "monthly"}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[280px]">
                            {(r.active_modules ?? []).map((m) => <Badge key={m} variant="secondary" className="text-[10px]">{m}</Badge>)}
                            {!r.active_modules?.length && <span className="text-xs text-muted-foreground">—</span>}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">€{r.total.toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => openEdit(r)}>
                            <Settings2 className="mr-1 h-3 w-3" />{t("pages.admin.modulesPage.edit")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">{t("pages.admin.noUsers")}</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageBody>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("pages.admin.modulesPage.editTitle", { email: editing?.email ?? "" })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">{t("pages.admin.modulesPage.plan")}</label>
                <Select value={plan} onValueChange={(v) => setPlan(v as BasePlanId)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BASE_PLANS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (€{p.monthlyPrice})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">{t("pages.admin.modulesPage.cycle")}</label>
                <Select value={cycle} onValueChange={(v) => setCycle(v as BillingCycle)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t("pages.admin.modulesPage.cycleMonthly")}</SelectItem>
                    <SelectItem value="annual">{t("pages.admin.modulesPage.cycleAnnual")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">{t("pages.admin.modulesPage.activeModules")}</label>
              <div className="mt-2 grid max-h-[40vh] grid-cols-2 gap-2 overflow-auto rounded border p-2">
                {MODULES.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 rounded p-1.5 hover:bg-muted/40 cursor-pointer">
                    <Checkbox checked={mods.includes(m.id)} onCheckedChange={() => toggle(m.id)} />
                    <span className="text-sm flex-1">{m.name}</span>
                    <Badge variant="outline" className="text-[9px]">{m.tier}</Badge>
                    <span className="text-xs text-muted-foreground">€{m.monthlyPrice}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <div className="flex justify-between"><span>{t("pages.admin.modulesPage.computedTotal")}</span><strong>€{localBreakdown.total.toFixed(2)}/mese</strong></div>
              {recomputed !== null && (
                <div className="flex justify-between text-emerald-600"><span>{t("pages.admin.modulesPage.serverTotal")}</span><strong>€{recomputed.toFixed(2)}/mese</strong></div>
              )}
              <div className="text-xs text-muted-foreground mt-1">
                {t("pages.admin.modulesPage.bundleSummary", { n: localBreakdown.selectedModules.length, bundle: localBreakdown.bundleDiscountPct, cycle: localBreakdown.cycleDiscountPct })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={recompute}>{t("pages.admin.modulesPage.recomputeServer")}</Button>
            <Button onClick={save} disabled={saving}>{saving ? t("pages.admin.modulesPage.saving") : t("pages.admin.modulesPage.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
